import { useCallback, useEffect, useState } from "react";
import { useToast } from "@/app/providers/ToastProvider";
import {
  deleteTransferHistory,
  listTransferHistory,
  listTransferQueue,
  retransferHistory,
  type TransferHistoryItem,
  type TransferJob,
} from "@/features/transfer/api";
import { cn } from "@/shared/lib/cn";
import { proxyImageUrl } from "@/shared/lib/image";
import { Button } from "@/shared/ui/Button";
import { EmptyState } from "@/shared/ui/EmptyState";
import { Input } from "@/shared/ui/Input";
import { PageHeader } from "@/shared/ui/PageHeader";
import { Spinner } from "@/shared/ui/Spinner";

type StatusFilter = "all" | "ok" | "fail";

/** 整理历史：筛选 / 重试 / 删记录 */
export function TransferPage() {
  const { toast } = useToast();
  const [items, setItems] = useState<TransferHistoryItem[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [filter, setFilter] = useState<StatusFilter>("all");
  const [title, setTitle] = useState("");
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<number | null>(null);
  const [queue, setQueue] = useState<TransferJob[]>([]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const status =
        filter === "ok" ? true : filter === "fail" ? false : null;
      const [hist, q] = await Promise.all([
        listTransferHistory({ page, count: 30, status, title: query || undefined }),
        listTransferQueue().catch(() => [] as TransferJob[]),
      ]);
      setItems(hist?.list ?? []);
      setTotal(hist?.total ?? 0);
      setQueue(Array.isArray(q) ? q : []);
    } catch (e) {
      setError(e instanceof Error ? e.message : "加载失败");
    } finally {
      setLoading(false);
    }
  }, [page, filter, query]);

  useEffect(() => {
    void load();
  }, [load]);

  async function withBusy(id: number, fn: () => Promise<void>) {
    setBusyId(id);
    try {
      await fn();
    } catch (e) {
      toast(e instanceof Error ? e.message : "操作失败", "error");
    } finally {
      setBusyId(null);
    }
  }

  const pages = Math.max(1, Math.ceil(total / 30));

  return (
    <div>
      <PageHeader
        title="整理"
        description={`${total} 条记录${queue.length ? ` · 队列 ${queue.length}` : ""}`}
        actions={
          <Button variant="ghost" onClick={() => void load()} disabled={loading}>
            刷新
          </Button>
        }
      />

      <div className="mb-4 flex flex-wrap items-center gap-2">
        {(
          [
            ["all", "全部"],
            ["ok", "成功"],
            ["fail", "失败"],
          ] as const
        ).map(([key, label]) => (
          <button
            key={key}
            type="button"
            onClick={() => {
              setPage(1);
              setFilter(key);
            }}
            className={cn(
              "h-8 rounded-[var(--radius-sm)] border px-3 text-[13px]",
              filter === key
                ? "border-brand/40 bg-brand-muted text-brand"
                : "border-border text-fg-muted hover:bg-bg-hover",
            )}
          >
            {label}
          </button>
        ))}
        <form
          className="flex min-w-[200px] flex-1 gap-2"
          onSubmit={(e) => {
            e.preventDefault();
            setPage(1);
            setQuery(title.trim());
          }}
        >
          <Input
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="标题筛选"
            maxLength={80}
          />
          <Button type="submit" variant="ghost">
            筛选
          </Button>
        </form>
      </div>

      {queue.length > 0 ? (
        <div className="mb-4 rounded-[var(--radius-md)] border border-border bg-bg-card px-3 py-2 text-[12px] text-fg-muted">
          整理队列进行中：{queue.length} 项
        </div>
      ) : null}

      {loading ? (
        <div className="flex items-center gap-2 text-[13px] text-fg-muted">
          <Spinner /> 加载中…
        </div>
      ) : null}

      {error ? (
        <div className="mb-4 rounded-[var(--radius-md)] border border-danger/30 bg-danger/10 px-4 py-3 text-[13px] text-danger">
          {error}
        </div>
      ) : null}

      {!loading && !error && items.length === 0 ? (
        <EmptyState title="暂无整理记录" />
      ) : null}

      <ul className="flex flex-col gap-2">
        {items.map((h) => {
          const busy = busyId === h.id;
          return (
            <li
              key={h.id}
              className="flex gap-3 rounded-[var(--radius-md)] border border-border bg-bg-card p-3"
            >
              <div className="h-[72px] w-[48px] shrink-0 overflow-hidden rounded-[var(--radius-sm)] bg-bg-hover">
                {proxyImageUrl(h.image) ? (
                  <img
                    src={proxyImageUrl(h.image)}
                    alt=""
                    className="h-full w-full object-cover"
                    loading="lazy"
                    referrerPolicy="no-referrer"
                  />
                ) : null}
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-baseline gap-2">
                  <h3 className="truncate text-[14px] font-medium">
                    {h.title || "未命名"}
                  </h3>
                  <span
                    className={cn(
                      "rounded px-1.5 py-0.5 text-[11px]",
                      h.status
                        ? "bg-success/15 text-success"
                        : "bg-danger/15 text-danger",
                    )}
                  >
                    {h.status ? "成功" : "失败"}
                  </span>
                </div>
                <p className="mt-0.5 text-[12px] text-fg-subtle">
                  {[
                    h.date,
                    h.type,
                    h.seasons,
                    h.episodes,
                    h.mode,
                  ]
                    .filter(Boolean)
                    .join(" · ")}
                </p>
                {!h.status && h.errmsg ? (
                  <p className="mt-1 line-clamp-2 text-[12px] text-danger">
                    {h.errmsg}
                  </p>
                ) : null}
                <p className="mt-1 truncate text-[11px] text-fg-subtle" title={h.src || ""}>
                  {h.src}
                </p>
                <div className="mt-2 flex flex-wrap gap-2">
                  {!h.status ? (
                    <Button
                      variant="ghost"
                      disabled={busy}
                      onClick={() =>
                        void withBusy(h.id, async () => {
                          await retransferHistory(h.id);
                          toast("已提交重新整理", "success");
                          await load();
                        })
                      }
                    >
                      重试
                    </Button>
                  ) : null}
                  <Button
                    variant="danger"
                    disabled={busy}
                    onClick={() => {
                      if (!window.confirm("仅删除记录，不删除磁盘文件。继续？"))
                        return;
                      void withBusy(h.id, async () => {
                        await deleteTransferHistory(h.id);
                        toast("已删除记录", "success");
                        await load();
                      });
                    }}
                  >
                    删记录
                  </Button>
                </div>
              </div>
            </li>
          );
        })}
      </ul>

      {pages > 1 ? (
        <div className="mt-4 flex items-center justify-center gap-2">
          <Button
            variant="ghost"
            disabled={page <= 1 || loading}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
          >
            上一页
          </Button>
          <span className="text-[13px] text-fg-muted">
            {page} / {pages}
          </span>
          <Button
            variant="ghost"
            disabled={page >= pages || loading}
            onClick={() => setPage((p) => p + 1)}
          >
            下一页
          </Button>
        </div>
      ) : null}
    </div>
  );
}
